/**
 * What the HTTP client knows about the browser's cookie session
 * (`docs/components/accounts.md`, Transports).
 *
 * The session token travels in an HttpOnly cookie that scripts cannot read. A cookie session
 * must also send its CSRF token in `X-CSRF-Token` on every unsafe method; the server hands it
 * out with the session. The session provider remembers it here, and the client adds it.
 *
 * When the server answers `authentication_required`, the session has ended (expired, or
 * ended elsewhere). The client reports that here, and the session provider listens.
 */
export type HttpMethod = "GET" | "HEAD" | "OPTIONS" | "POST" | "PUT" | "PATCH" | "DELETE";

export const CSRF_TOKEN_HEADER = "X-CSRF-Token";

/** Methods that change nothing; the server checks the CSRF token on all others. */
const SAFE_METHODS: ReadonlySet<HttpMethod> = new Set<HttpMethod>(["GET", "HEAD", "OPTIONS"]);

let rememberedCsrfToken: string | null = null;
let sessionEndedListener: (() => void) | null = null;

/** Set while a session is signed in; `null` once it is not. */
export function rememberCsrfToken(csrfToken: string | null): void {
  rememberedCsrfToken = csrfToken;
}

/** The header to send with a request of `method`, or `null` if none is needed or known. */
export function csrfHeaderFor(
  method: HttpMethod,
  csrfToken: string | null,
): readonly [name: string, value: string] | null {
  if (SAFE_METHODS.has(method) || csrfToken === null) {
    return null;
  }
  return [CSRF_TOKEN_HEADER, csrfToken];
}

export function csrfHeaderForRequest(method: HttpMethod): readonly [name: string, value: string] | null {
  return csrfHeaderFor(method, rememberedCsrfToken);
}

/** One listener at a time: the session provider. Pass `null` to stop listening. */
export function listenForEndedSession(listener: (() => void) | null): void {
  sessionEndedListener = listener;
}

export function reportEndedSession(): void {
  sessionEndedListener?.();
}
